// components/data-sources/connectorCatalog/useConnectorCatalog.ts

import { useCallback, useEffect, useRef, useState } from "react";

import { listConnectors } from "@/api/managedSources";
import type { ManagedSourceConnector } from "@/types/managedSources";

export type ConnectorCatalogState = {
  connectors: ManagedSourceConnector[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Flip one tile to `requested` after a successful request, without a refetch. */
  markRequested: (connectorType: string, requestedAt: string) => void;
};

/**
 * The connectors endpoint, read each time `enabled` turns true (the picker
 * opens). The tile state is per account and per request on the server
 * (ADR-0410), so a cached copy from an earlier open could show a tile the
 * account can no longer use.
 */
export function useConnectorCatalog(enabled: boolean): ConnectorCatalogState {
  const [connectors, setConnectors] = useState<ManagedSourceConnector[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);
    listConnectors()
      .then((list) => {
        if (ticket !== latest.current) return;
        setConnectors(Array.isArray(list) ? list : []);
      })
      .catch((err: unknown) => {
        if (ticket !== latest.current) return;
        setConnectors([]);
        setError(err instanceof Error && err.message ? err.message : "Request failed");
      })
      .finally(() => {
        if (ticket === latest.current) setLoading(false);
      });
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const markRequested = useCallback((connectorType: string, requestedAt: string) => {
    setConnectors((prev) =>
      prev.map((c) =>
        c.connector_type === connectorType
          ? { ...c, tile_state: "requested", requested_at: requestedAt }
          : c
      )
    );
  }, []);

  return { connectors, loading, error, reload, markRequested };
}
